import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  LightShowConfig,
  LightShowLook,
  UserEventStates,
} from "@max/common/event";
import { useDataContext } from "../DataContext";
import { useEventConfig } from "../EventConfig";
import { useServerClockOffset } from "./clock";
import { useRequestAnimationFrame } from "../useRequestAnimationFrame";
import {
  getExitStroke,
  preloadLook,
  renderLook,
  setCassetteLoader,
  setConfettiLoader,
  setLottieLoader,
} from "@max/common/lightshow/render";
import { useChromeColor, useWakeLock } from "./hooks";
import { isLightShowDebug, recordDebug, recordFrame } from "./debug";
import { DebugOverlay } from "./DebugOverlay";
import { LiveChange, useAblyLightShow } from "./ably";
import phoneGlowAnimated from "./PhoneGlowAnimated.svg";

// Dynamic imports here keep the confetti artwork, the cassette and the
// animation player out of the main bundle.
setConfettiLoader(() =>
  import("@max/common/lightshow/confetti").then((m) => m.CONFETTI_TILE_SVG),
);
setCassetteLoader(() =>
  import("@max/common/lightshow/cassette").then((m) => m.CASSETTE_IMAGE),
);
setLottieLoader(() =>
  import("lottie-web/build/player/lottie_light_canvas").then((m) => m.default),
);

/**
 * idle:       no look is live
 * prompt:     a look went live, asking the fan to join
 * checkingIn: gated show, the check-in form is open over the page
 * joining:    two frames with nothing over the page edges, see below
 * joined:     full-screen takeover
 * out:        fan declined or exited; a bar lets them rejoin instantly
 */
type FanState = "idle" | "prompt" | "checkingIn" | "joining" | "joined" | "out";

const CONTROLS_TIMEOUT = 3000;

const LATO_STYLESHEET =
  "https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap";

interface LightShowProps {
  registerOpen: boolean;
  onCheckIn: () => void;
  children?: ReactNode;
}

interface LightShowContextValue {
  /** The fan declined or exited a live show; templates show the rejoin bar. */
  showRejoin: boolean;
  needsCheckIn: boolean;
  join: () => void;
}

const LightShowContext = createContext<LightShowContextValue>({
  showRejoin: false,
  needsCheckIn: false,
  join: () => {},
});

export const useLightShow = () => useContext(LightShowContext);

/**
 * Follows the live look show runners set from Backstage, pushed over the
 * event's Ably channel (see useAblyLightShow), for events that publish a
 * light show.
 */
export const LightShowProvider = ({
  disabled,
  children,
  ...props
}: LightShowProps & { disabled?: boolean }) => {
  if (disabled) {
    return <>{children}</>;
  }

  return <LiveLightShow {...props}>{children}</LiveLightShow>;
};

interface ScheduledChange {
  look?: LightShowLook;
  /** Server time the look starts; its animation is timed from here. */
  startAtMs: number;
}

/**
 * Applies a show runner's change at `startedAt + startDelayMs` on the server
 * clock, so every phone that has the update switches on the same frame. A
 * phone that hears about it late applies it at once, already in step.
 */
const useScheduledChange = (
  change: LiveChange | undefined,
  clockOffsetMs: number,
) => {
  const [current, setCurrent] = useState<ScheduledChange>({ startAtMs: 0 });
  // For debugging: each change's apply is recorded once, not again when a
  // clock resync re-runs this.
  const recordedChange = useRef<LiveChange>();

  useEffect(() => {
    const serverNow = Date.now() + clockOffsetMs;
    const next: ScheduledChange = {
      look: change?.look ?? undefined,
      startAtMs: change ? change.startedAtMs + change.startDelayMs : serverNow,
    };
    const apply = () => {
      setCurrent(next);
      if (isLightShowDebug && change && recordedChange.current !== change) {
        recordedChange.current = change;
        recordDebug({
          lastApply: {
            lookName: next.look?.name,
            scheduledMs: next.startAtMs,
            appliedMs: Date.now() + clockOffsetMs,
          },
        });
      }
    };
    const wait = next.startAtMs - serverNow;
    if (wait <= 0) {
      apply();
      return;
    }
    const timer = window.setTimeout(apply, wait);
    return () => window.clearTimeout(timer);
  }, [change, clockOffsetMs]);

  return current;
};

const LiveLightShow = ({ children, ...props }: LightShowProps) => {
  const { eventId, lightShow } = useEventConfig();

  const clockOffsetMs = useServerClockOffset(!!lightShow?.enabled);
  // Live changes are pushed over Ably; see useAblyLightShow.
  const change = useAblyLightShow(eventId, !!lightShow?.enabled, clockOffsetMs);
  const current = useScheduledChange(change, clockOffsetMs);

  // Load the artwork some looks fetch on demand (confetti, the cassette)
  // ahead of the show, so a look shows the moment it goes live.
  useEffect(() => {
    if (lightShow?.enabled) lightShow.looks?.forEach(preloadLook);
  }, [lightShow]);

  return (
    <>
      <LightShowController
        look={current.look}
        startedAtMs={current.startAtMs}
        clockOffsetMs={clockOffsetMs}
        gate={lightShow?.gate}
        {...props}
      >
        {children}
      </LightShowController>
      {isLightShowDebug && <DebugOverlay clockOffsetMs={clockOffsetMs} />}
    </>
  );
};

const enterFullscreen = () => {
  try {
    document.documentElement.requestFullscreen?.()?.catch(() => {});
  } catch (e) {
    // iPhone Safari has no element fullscreen; the fixed takeover covers the page.
  }
};

const exitFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => {});
  }
};

const LightShowController = ({
  look,
  startedAtMs,
  clockOffsetMs,
  gate,
  registerOpen,
  onCheckIn,
  children,
}: LightShowProps & {
  /** The live look, or undefined when no show is running. */
  look?: LightShowLook;
  startedAtMs: number;
  /** Added to `Date.now()` to match the clock `startedAtMs` is on. */
  clockOffsetMs: number;
  gate: LightShowConfig["gate"];
}) => {
  const { t } = useTranslation();
  const { data } = useDataContext();
  const [fanState, setFanState] = useState<FanState>("idle");
  const [seed] = useState(() => Math.random());

  const active = !!look;
  const needsCheckIn =
    gate === "checkin" && data.status === UserEventStates.draft;

  const inShow = fanState === "joining" || fanState === "joined";

  useWakeLock(fanState === "joined");
  useEffect(() => recordDebug({ fanState }), [fanState]);
  // Black around the takeover so the browser's status bar and toolbar areas
  // frame the look instead of showing the template's page background.
  useChromeColor(inShow ? "#000000" : undefined);

  // A new show prompts everyone who isn't already in; switching looks while
  // live keeps fans where they are.
  useEffect(() => {
    setFanState((prev) =>
      !active ? "idle" : prev === "idle" ? "prompt" : prev,
    );
  }, [active]);

  useEffect(() => {
    if (!inShow) exitFullscreen();
  }, [inShow]);

  // iOS 26 Safari picks its status bar/toolbar color when a fixed layer starts
  // covering the screen edges, and ignores one fixed layer being swapped for
  // another. Going straight from the prompt to the takeover kept the prompt's
  // (white) color, so the takeover waits two frames with no edge layer.
  useEffect(() => {
    if (fanState !== "joining") return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setFanState("joined"));
    });
    return () => cancelAnimationFrame(frame);
  }, [fanState]);

  // Per the comps, checking in leads straight into the look.
  useEffect(() => {
    if (fanState !== "checkingIn") return;
    if (!needsCheckIn) {
      setFanState("joining");
    } else if (!registerOpen) {
      setFanState("out");
    }
  }, [fanState, needsCheckIn, registerOpen]);

  const handleJoin = () => {
    if (needsCheckIn) {
      setFanState("checkingIn");
      onCheckIn();
      return;
    }
    // Fullscreen needs a user gesture, so it's requested from the tap itself.
    enterFullscreen();
    setFanState("joining");
  };

  const joinLabel = needsCheckIn ? t("Check In to Join") : t("Join");

  return (
    <LightShowContext.Provider
      value={{
        showRejoin: fanState === "out",
        needsCheckIn,
        join: handleJoin,
      }}
    >
      {children}
      {fanState === "joined" && look && (
        <Takeover
          look={look}
          startedAtMs={startedAtMs}
          clockOffsetMs={clockOffsetMs}
          seed={seed}
          onExit={() => setFanState("out")}
        />
      )}
      {fanState === "prompt" && (
        <Prompt role="dialog" aria-modal="true">
          {/* The comp uses Lato; not every template loads it (Round requests
              an unavailable weight), so the prompt loads its own. */}
          <link rel="stylesheet" href={LATO_STYLESHEET} />
          <PromptContent>
            <PromptIcon />
            <PromptTitle>
              {look?.title || t("Join the Light Show?")}
            </PromptTitle>
            <PromptBody>
              {look?.subtitle ||
                t("Turn your brightness up and hold your phone high.")}
            </PromptBody>
            {/* Always shown, whatever subtitle the look sets. */}
            <PromptSupport>
              {t("Tap the screen anytime to exit.")}
            </PromptSupport>
          </PromptContent>
          <PromptActions>
            <JoinButton onClick={handleJoin}>{joinLabel}</JoinButton>
            <NotNowButton onClick={() => setFanState("out")}>
              {t("Not Now")}
            </NotNowButton>
          </PromptActions>
        </Prompt>
      )}
    </LightShowContext.Provider>
  );
};

const Takeover = ({
  look,
  startedAtMs,
  clockOffsetMs,
  seed,
  onExit,
}: {
  look: LightShowLook;
  startedAtMs: number;
  clockOffsetMs: number;
  seed: number;
  onExit: () => void;
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef({ w: 0, h: 0 });
  const hideTimer = useRef<number>();
  const [showControls, setShowControls] = useState(false);

  // Drawn at 1x pixel density: the crowd sees it from a distance, and it
  // halves the GPU work on high-DPI phones over a long show.
  useEffect(() => {
    const canvas = canvasRef.current!;
    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w;
      canvas.height = h;
      sizeRef.current = { w, h };
    };
    resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      window.clearTimeout(hideTimer.current);
    };
  }, []);

  const draw = useCallback(
    (_time: number, deltaMs: number) => {
      recordFrame(deltaMs);
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;
      // Seconds into the look on the server clock, so every phone draws the
      // same frame at the same moment.
      const elapsed = (Date.now() + clockOffsetMs - startedAtMs) / 1000;
      renderLook(
        ctx,
        sizeRef.current.w,
        sizeRef.current.h,
        elapsed,
        look,
        seed,
      );
    },
    [look, startedAtMs, clockOffsetMs, seed],
  );

  useRequestAnimationFrame(draw);

  // Like mobile video controls: tapping anywhere reveals the exit button.
  const revealControls = () => {
    setShowControls(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(
      () => setShowControls(false),
      CONTROLS_TIMEOUT,
    );
  };

  const stroke = getExitStroke(look);

  return (
    <TakeoverRoot onClick={revealControls}>
      <Canvas ref={canvasRef} />
      <ExitButton
        aria-label={t("Exit Light Show") as string}
        $visible={showControls}
        $stroke={stroke}
        onClick={(e) => {
          e.stopPropagation();
          if (showControls) onExit();
          else revealControls();
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </ExitButton>
    </TakeoverRoot>
  );
};

// Rendered as an <img> so the SVG's own CSS animations (glowing screen and
// rays, with a reduced-motion fallback) run untouched.
const PromptIcon = () => (
  <img src={phoneGlowAnimated} width={82} height={53} alt="" />
);

const TakeoverRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1001;
  background: #000;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
`;

// iOS 26 Safari ignores theme-color and colors its status bar and toolbar from
// the CSS background of what covers the screen edges, not from drawn pixels.
// Keep the takeover's layers black so Safari's bars stay black.
const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
  background-color: #000;
`;

const ExitButton = styled.button<{ $visible: boolean; $stroke?: string }>`
  position: absolute;
  left: 50%;
  bottom: calc(40px + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: ${({ $stroke }) => ($stroke ? `3px solid ${$stroke}` : "none")};
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ease;
`;

// Prompt styles follow the "Triggered" comp. Figma doesn't export the dim and
// blur over the page, so those are approximated.
const Prompt = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 24px calc(32px + env(safe-area-inset-bottom));
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #fff;
  text-align: center;
  font-family: "Lato", sans-serif;
  font-weight: 700;
`;

const PromptContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PromptTitle = styled.h2`
  max-width: 342px;
  margin: 32px 0 16px;
  font-size: 40px;
  font-weight: 700;
  line-height: normal;
`;

const PromptBody = styled.p`
  max-width: 336px;
  margin: 0;
  font-size: 20px;
  line-height: normal;
  /* Keep line breaks from the builder's multi-line subtitle. */
  white-space: pre-line;
`;

// Same style as the subtitle, one blank line below it as in the comp.
const PromptSupport = styled(PromptBody)`
  margin-top: 1.2em;
`;

const PromptActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;
  width: 100%;
  max-width: 345px;
`;

const JoinButton = styled.button`
  height: 94px;
  border: none;
  border-radius: 50px;
  background: #ffffff;
  color: #000000;
  font-family: inherit;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
`;

const NotNowButton = styled.button`
  height: 59px;
  border: 2px solid #ffffff;
  border-radius: 50px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-family: inherit;
  font-size: 20px;
  font-weight: 700;
`;
